import { inject, Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent } from '../component/snackbar/snackbar.component';
import { ErrorService } from '../../../../../../libs/shared/src/services/error.service';
import { TranslocoService } from '@jsverse/transloco';
import { translateServerMessage } from '../functions/extract-server-error';

@Injectable({ providedIn: 'root' })
export class ServerHttpInterceptor implements HttpInterceptor {
  private errorService = inject(ErrorService);
  private snackBar = inject(MatSnackBar);
  private transloco = inject(TranslocoService);

  // OIDC/auth rute ignorišemo za toaste i greške
  private readonly authPathsToIgnore = [
    '/.well-known/openid-configuration',
    '/connect/authorize',
    '/connect/token',
    '/token', // OpenIddict token endpoint
    '/Token', // case variation
    '/connect/logout',
    '/auth/logout',
    '/Auth/logout',
    '/connect/userinfo',
    '/connect/revocation',
    '/connect/introspect',
    '/auth/login',
    '/auth/refresh',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/change-password',
    '/Auth/change-password',
    '/auth/restore-account',
    '/Auth/restore-account',
    '/auth/restore-clinic',
    '/Auth/restore-clinic',
    '/auth/request-restore-clinic',
    '/Auth/request-restore-clinic',
    '/auth/confirm-forgot-password',
    '/Auth/confirm-forgot-password',
    '/auth/request-claim-account',
    '/Auth/request-claim-account',
  ];

  private readonly successToastPathsToIgnore = ['/Vat/calculate-net'];

  private readonly notFoundListGetPathsToIgnore = ['/VideoTutorials'];

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const endpoint = req.url.split('?')[0];

    return next.handle(req).pipe(
      // SUCCESS toast samo za POST koji NIJE auth/discovery
      tap((event) => {
        if (
          event instanceof HttpResponse &&
          event.status >= 200 &&
          event.status < 300 &&
          req.method === 'POST' &&
          !this.isAuthPath(endpoint) &&
          !this.shouldSkipSuccessToast(endpoint) &&
          !this.isSchedulingRequestReviewPost(endpoint, req.method)
        ) {
          this.showSnackbar(
            this.transloco.translate('You have successfully saved!'),
            'success',
          );
        }
      }),

      catchError((error: HttpErrorResponse) => {
        // When responseType:'blob' is used, Angular deserializes even error bodies
        // as Blob — parse it to JSON text first so the switch below can read detail/title.
        if (
          error.error instanceof Blob &&
          error.error.type !== 'application/pdf'
        ) {
          return from(error.error.text()).pipe(
            switchMap((text) => {
              let parsed: HttpErrorResponse = error;
              try {
                const json = JSON.parse(text);
                parsed = new HttpErrorResponse({
                  error: json,
                  headers: error.headers,
                  status: error.status,
                  statusText: error.statusText,
                  url: error.url ?? undefined,
                });
              } catch {
                /* not JSON — keep original */
              }
              return this.handleError(parsed, endpoint, req, next);
            }),
            catchError(() => this.handleError(error, endpoint, req, next)),
          );
        }

        return this.handleError(error, endpoint, req, next);
      }),
    );
  }

  private handleError(
    error: HttpErrorResponse,
    endpoint: string,
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<never> {
    this.errorService.clearServerErrors();

    // 401: refresh interceptor handles token refresh / logout — skip here
    if (error.status === 401) {
      return throwError(() => error);
    }

    // 403: show a generic permission error (refresh interceptor doesn't handle 403)
    if (error.status === 403) {
      if (!this.isAuthPath(endpoint)) {
        this.showSnackbar(
          this.transloco.translate(
            'You do not have permission to perform this action.',
          ),
          'error',
        );
      }
      return throwError(() => error);
    }

    switch (error.status) {
      case 400: {
        const messages: string[] = [];
        const err = error.error;

        if (err?.errors) {
          for (const value of Object.values(err.errors)) {
            if (Array.isArray(value)) messages.push(...(value as string[]));
            else messages.push(String(value));
          }
        }

        // Prioritet: detail > validacione greške > title > fallback
        let msg: string = err?.detail
          ? this.translateKnownServerMessage(err.detail)
          : '';

        if (!msg && messages.length) {
          msg = messages.map((m) => this.translateFvMessage(m)).join(' • ');
        }

        if (!msg) {
          msg = err?.title || 'Bad request';
        }

        if (!this.isAuthPath(endpoint)) {
          this.showSnackbar(msg, 'error');
        }
        this.errorService.publishValidationServerErrors(messages);
        break;
      }

      case 404: {
        // Backend vraca detail oblika: Entity "X" (guid) was not found.
        // To je interno (ne prevodi se), pa fallback-ujemo na prevodljiv title kljuc.
        const rawDetail: string | undefined = error.error?.detail;
        const isEntityNotFound =
          typeof rawDetail === 'string' &&
          /^Entity "(.+)" \(.+\) was not found\.$/.test(rawDetail);
        const msg = isEntityNotFound
          ? 'The specified resource was not found.'
          : this.translateKnownServerMessage(
              error.error?.title ??
                rawDetail ??
                'The specified resource was not found.',
            );
        if (!this.shouldIgnoreNotFoundListGet(endpoint, req.method)) {
          this.maybeShowError(endpoint, req, next, msg);
        }
        this.errorService.publishNotFoundServerError(msg);
        break;
      }

      case 409: {
        const messages: string[] = [];
        const err = error.error;
        if (err?.errors) {
          for (const value of Object.values(err.errors)) {
            if (Array.isArray(value)) messages.push(...(value as string[]));
            else messages.push(String(value));
          }
        }

        // Prioritet: detail > validacione poruke > title > fallback
        let msg = err?.detail;
        let paramsData = null;

        // Pokusaj parsiranja JSON-a iz detail polja (za parametrizovane poruke)
        if (msg) {
          try {
            const parsed = JSON.parse(msg);
            if (parsed && parsed.key) {
              msg = parsed.key;
              paramsData = parsed.paramsData;
            }
          } catch (e) {
            // Nije JSON, proveravamo da li je string koji treba parsirati regexom
            const workScheduleRegex =
              /Work schedule for veterinarian (.*) already exists for the selected period and shift\./;
            const match = msg.match(workScheduleRegex);
            if (match) {
              msg = 'workScheduleConflict';
              const fullName = match[1];
              const nameParts = fullName.split(' ');
              paramsData = {
                firstName: nameParts[0],
                lastName: nameParts.slice(1).join(' '),
              };
            }

            const diagnosisMatch = msg.match(
              /^Diagnosis '(.+)' already exists\.$/,
            );
            if (diagnosisMatch) {
              msg = 'diagnosisAlreadyExists';
              paramsData = { name: diagnosisMatch[1] };
            }

            const medicineMatch = msg.match(
              /^Medicine '(.+)' already exists\.$/,
            );
            if (medicineMatch) {
              msg = 'medicineAlreadyExists';
              paramsData = { name: medicineMatch[1] };
            }

            const serviceMatch = msg.match(/^Service '(.+)' already exists\.$/);
            if (serviceMatch) {
              msg = 'serviceAlreadyExists';
              paramsData = { name: serviceMatch[1] };
            }

            const cashRegisterCodeMatch = msg.match(
              /^Cash register code '(.+)' already exists for this clinic\.$/,
            );
            if (cashRegisterCodeMatch) {
              msg = 'cashRegisterCodeAlreadyExistsForClinic';
              paramsData = { code: cashRegisterCodeMatch[1] };
            }

            const manufacturerMatch = msg.match(
              /^Manufacturer '(.+)' already exists\.$/,
            );
            if (manufacturerMatch) {
              msg = 'manufacturerAlreadyExists';
              paramsData = { name: manufacturerMatch[1] };
            }

            const typeMatch = msg.match(/^Type '(.+)' already exists\.$/);
            if (typeMatch) {
              msg = 'typeAlreadyExists';
              paramsData = { name: typeMatch[1] };
            }

            const serialNumbersMatch = msg.match(
              /^Serial numbers already exist: (.+)$/,
            );
            if (serialNumbersMatch) {
              msg = 'serialNumbersAlreadyExist';
              paramsData = { numbers: serialNumbersMatch[1] };
            }

            if (
              msg === 'schedulingSlotTaken' ||
              msg ===
                'This appointment time was just taken. Please choose another time.'
            ) {
              msg = 'schedulingSlotTaken';
            }
          }
        }

        if (!msg && messages.length) {
          msg = messages.join(' • ');
        }
        if (!msg) {
          msg = err?.title || 'Conflict';
        }

        if (!this.isAuthPath(endpoint) && !this.isSchedulingRequestReviewPost(endpoint, req.method)) {
          this.showSnackbar(msg, 'error', paramsData);
        }
        this.errorService.publishValidationServerErrors(messages);
        break;
      }

      case 500: {
        const rawDetail: string | undefined = error.error?.detail;
        const isEntityNotFound =
          typeof rawDetail === 'string' &&
          /^Entity "(.+)" \(.+\) was not found\.$/.test(rawDetail);
        const msg = isEntityNotFound
          ? 'The specified resource was not found.'
          : this.translateKnownServerMessage(
              rawDetail ?? error.error?.title ?? 'Ops... Something went wrong!',
            );
        this.maybeShowError(endpoint, req, next, msg);
        this.errorService.publishInternalServerError(msg);
        break;
      }

      case 0: {
        // Stvarna mrežna greška - server nije dostupan (CORS/SSL/timeout)
        if (!this.isAuthPath(endpoint)) {
          this.showRetrySnackbar(
            req,
            next,
            'Server is not reachable, please try again.',
          );
        }
        break;
      }

      default: {
        // Neočekivani status (415, 422, 429 itd.) - prikaži stvarnu grešku sa servera
        if (!this.isAuthPath(endpoint)) {
          const rawDetail: string | undefined = error.error?.detail;
          const isEntityNotFound =
            typeof rawDetail === 'string' &&
            /^Entity "(.+)" \(.+\) was not found\.$/.test(rawDetail);
          const msg = isEntityNotFound
            ? 'The specified resource was not found.'
            : this.translateKnownServerMessage(
                rawDetail ||
                  error.error?.title ||
                  'An unexpected error occurred.',
              );
          this.showSnackbar(msg, 'error');
        }
        break;
      }
    }

    return throwError(() => error);
  }

  private isAuthPath(url: string): boolean {
    return this.authPathsToIgnore.some((p) => url.includes(p));
  }

  private shouldSkipSuccessToast(url: string): boolean {
    return this.successToastPathsToIgnore.some((p) => url.includes(p));
  }

  private shouldIgnoreNotFoundListGet(url: string, method: string): boolean {
    if (method !== 'GET') return false;
    return this.notFoundListGetPathsToIgnore.some((p) => url.endsWith(p));
  }

  /** Review dialog shows its own error snackbar for 409 slot conflicts. */
  private isSchedulingRequestReviewPost(url: string, method: string): boolean {
    return (
      method === 'POST' &&
      /\/Scheduling\/SchedulingRequests\/[^/]+\/Review$/.test(url)
    );
  }

  private maybeShowError(
    endpoint: string,
    req: HttpRequest<any>,
    next: HttpHandler,
    message: string,
  ) {
    if (!this.isAuthPath(endpoint)) {
      this.showRetrySnackbar(req, next, message);
    }
  }

  private showSnackbar(
    message: string,
    type: 'success' | 'error' = 'error',
    params: any = null,
  ) {
    const config: any = {
      data: { message, buttonType: 'info', action: ['Close'], params },
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass:
        type === 'success' ? ['success-snackbar'] : ['snackbar-error'],
      duration: 5000,
    };
    this.snackBar.openFromComponent(SnackbarComponent, config);
  }

  private showRetrySnackbar(
    req: HttpRequest<any>,
    next: HttpHandler,
    message: string,
  ) {
    const config: any = {
      data: {
        message,
        buttonType: 'retry',
        action: ['Close', 'Retry'],
      },
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['snackbar-error'],
    };

    const ref = this.snackBar.openFromComponent(SnackbarComponent, config);

    // Jednostavan retry bez rekurzije: direktno ponovo pošalji originalni request
    ref
      .onAction()
      .pipe(take(1))
      .subscribe(() => {
        // Direktno ponovi request bez delay-a
        next
          .handle(req)
          .pipe(take(1))
          .subscribe({
            next: () => {},
            error: () => {},
          });
      });
  }

  private translateKnownServerMessage(msg: string): string {
    return translateServerMessage(msg);
  }

  private readonly fvFieldNames: Record<string, string> = {
    VAT: 'VAT',
    Name: 'Name',
    Price: 'Price',
    Email: 'Email',
    Discount: 'Discount',
    Value: 'Value',
    FirstName: 'First name',
    LastName: 'Last name',
    MobileNumber: 'Mobile number',
    BirthDateTime: 'Date of birth',
    BirthDateText: 'Date of birth',
    Gender: 'Gender',
    BreedUid: 'Breed',
    SpeciesUid: 'Species',
    AnimalUid: 'Animal',
    OwnerUid: 'Owner',
    ClinicUid: 'Clinic',
    VeterinarianUid: 'Veterinarian',
    DiagnosisUid: 'Diagnosis',
    ServiceUid: 'Service',
    ManufacturerUid: 'Manufacturer',
    TypeUid: 'Type',
    Description: 'Description',
    Anamnesis: 'Anamnesis',
    ClinicalExamination: 'Clinical examination',
    Title: 'Title',
    Category: 'Category',
    LicenceNumber: 'Licence number',
    TaxId: 'Tax ID',
    IdentificationNumber: 'Identification number',
    BankName: 'Bank name',
    CurrentAccount: 'Current account',
    CurrencyCode: 'Currency code',
    Contact: 'Contact',
    AppointmentTime: 'Appointment time',
    Pin: 'PIN',
    Password: 'Password',
    CurrentPassword: 'Current password',
    NewPassword: 'New password',
  };

  private translateFvMessage(msg: string): string {
    const t = (key: string, params?: Record<string, unknown>) =>
      this.transloco.translate(key, params);

    const translateField = (raw: string) => {
      const key = this.fvFieldNames[raw] ?? raw;
      const translated = t(key);
      return translated !== key ? translated : key;
    };

    // 'Field' must not be empty.
    const emptyMatch = msg.match(/^'(.+)' must not be empty\.$/);
    if (emptyMatch) {
      return t('fieldMustNotBeEmpty', { field: translateField(emptyMatch[1]) });
    }

    // 'Field' is not a valid email address.
    const emailMatch = msg.match(/^'(.+)' is not a valid email address\.$/);
    if (emailMatch) {
      return t('fieldInvalidEmail', { field: translateField(emailMatch[1]) });
    }

    // 'Field' must be greater than 'X'.
    const gtMatch = msg.match(/^'(.+)' must be greater than '(.+)'\.$/);
    if (gtMatch) {
      return t('fieldMustBeGreaterThan', {
        field: translateField(gtMatch[1]),
        value: gtMatch[2],
      });
    }

    // 'Field' must be greater than or equal to 'X'.
    const gteMatch = msg.match(
      /^'(.+)' must be greater than or equal to '(.+)'\.$/,
    );
    if (gteMatch) {
      return t('fieldMustBeGreaterThanOrEqual', {
        field: translateField(gteMatch[1]),
        value: gteMatch[2],
      });
    }

    // 'Field' must be less than or equal to 'X'.
    const lteMatch = msg.match(
      /^'(.+)' must be less than or equal to '(.+)'\.$/,
    );
    if (lteMatch) {
      return t('fieldMustBeLessThanOrEqual', {
        field: translateField(lteMatch[1]),
        value: lteMatch[2],
      });
    }

    return translateServerMessage(msg);
  }
}
